// src/components/Console/Staff/pages/UserPaymentDetails.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StateContext } from '../../../../StateProvider';
import { fetchUserData, updateTransaction, cancelTransaction } from '../../../../Utilities/api';
import { BackButton } from '../../../../Utilities/components/Buttons';
import './PaymentDetails.css';

function normalizeToArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return [String(val)];
}

function getCandidateRolesFromUser(u) {
  var out = [];
  if (!u) return out;
  if (u.role) out.push(String(u.role));
  if (u.job) out.push(String(u.job));
  if (u.jobs) out = out.concat(normalizeToArray(u.jobs));
  if (u.info && u.info.jobs) out = out.concat(normalizeToArray(u.info.jobs));
  return out;
}

function hasManagePermission(u, looseRole) {
  var allowed = { admin: true, manager: true, reception: true };
  if (looseRole && allowed[String(looseRole).toLowerCase()]) return true;

  var roles = getCandidateRolesFromUser(u);
  for (var i = 0; i < roles.length; i++) {
    var r = String(roles[i]).toLowerCase();
    if (allowed[r]) return true;
  }
  return false;
}

function camelCaseToPhrase(str) {
  return String(str)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, function (m) { return m.toUpperCase(); });
}

function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return 'N/A';
  var cleaned = ('' + phoneNumber).replace(/\D/g, '');
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) return match[1] + '-' + match[2] + '-' + match[3];
  return phoneNumber;
}

function formatMoney(n) {
  var num = parseFloat(n);
  if (isNaN(num)) num = 0;
  return '$' + num.toFixed(2);
}

function formatDateTime(s) {
  try { return new Date(s).toLocaleString(); } catch (e) { return 'N/A'; }
}

function toSafeLower(s) { return s ? String(s).toLowerCase() : ''; }

const UserPaymentDetails = function () {
  const navigate = useNavigate();
  const params = useParams();
  const userId = params.userId;
  const transactionId = params.transactionId;

  // current user & role from context
  const ctx = useContext(StateContext) || {};
  const currentUser = ctx.user || {};
  const currentRole = ctx.role;

  const [user, setUser] = useState({});
  const [transaction, setTransaction] = useState(null);
  const [notes, setNotes] = useState('');
  const [additionalServices, setAdditionalServices] = useState({});
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  var canManage = hasManagePermission(currentUser, currentRole);

  useEffect(function () {
    var mounted = true;
    async function fetchTransactionDetails() {
      setLoading(true);
      setError(null);
      try {
        const userData = await fetchUserData(userId);
        if (!mounted) return;

        if (userData) setUser(userData);

        var tid = parseInt(transactionId, 10);
        var found = null;
        if (userData && userData.Bookings && Array.isArray(userData.Bookings)) {
          for (var i = 0; i < userData.Bookings.length; i++) {
            var b = userData.Bookings[i];
            if (b && b.id === tid) { found = b; break; }
          }
        }

        if (found) {
          setTransaction(found);
          setNotes(found.specialNotes ? found.specialNotes : '');
          setAdditionalServices(found.additionalServices ? found.additionalServices : {});
        } else {
          setError('Transaction not found.');
        }
      } catch (err) {
        if (mounted) setError(err && err.message ? err.message : 'Failed to fetch transaction details.');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchTransactionDetails();
    return function cleanup(){ mounted = false; };
  }, [userId, transactionId]);

  if (loading) {
    return (
      <div className="pd-loading">
        <div className="pd-spinner" />
        <p className="pd-loading-text">Loading transaction details…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pd-error">
        <p><strong>Error:</strong> {error}</p>
        <BackButton />
      </div>
    );
  }

  if (!transaction) {
    return <div className="pd-empty">No transaction details available.</div>;
  }

  async function handleUpdate() {
    var updatedTransaction = {
      id: transaction.id,
      specialNotes: notes,
      additionalServices: additionalServices
    };
    try {
      await updateTransaction(transaction.id, updatedTransaction);
      navigate(-1);
    } catch (err) {
      setError('Failed to update transaction');
    }
  }

  function handleCancelClick() {
    setShowCancelModal(true);
  }

  async function confirmCancel() {
    setShowCancelModal(false);
    try {
      await cancelTransaction(transaction.id);
      navigate(-1);
    } catch (err) {
      setError('Failed to cancel transaction');
    }
  }

  function renderAdditionalGroup(title, obj) {
    if (!obj) return null;
    var keys = Object.keys(obj);
    if (keys.length === 0) return null;
    return (
      <div className="pd-addons-card">
        <h3 className="pd-addons-title">{title}</h3>
        <div className="pd-addons-grid">
          {keys.map(function (key) {
            var val = obj[key];
            return (
              <div key={key} className="pd-addons-item">
                <div className="pd-kv">
                  <span className="pd-k">{camelCaseToPhrase(key)}</span>
                  <span className="pd-v">{String(typeof val === 'boolean' ? val : val)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // privilege rules
  var canUpdate = canManage && transaction.status === 'Pending';
  var canCancel = canManage && transaction.status !== 'Canceled';

  // derived display
  var guests = 'N/A';
  if (transaction.selectedPets && Array.isArray(transaction.selectedPets) && transaction.selectedPets.length > 0) {
    guests = transaction.selectedPets.map(function (p) { return p && p.petName ? p.petName : ''; })
      .filter(Boolean).join(', ') || 'N/A';
  }

  var finalPrice = transaction.discountedPrice ? transaction.discountedPrice : transaction.originalPrice;

  function statusBadgeClass(s) {
    var k = toSafeLower(s);
    if (k === 'completed' || k === 'succeeded' || k === 'paid') return 'pd-badge pd-badge-success';
    if (k === 'pending' || k === 'authorized') return 'pd-badge pd-badge-warn';
    if (k === 'canceled' || k === 'voided' || k === 'failed' || k === 'refunded') return 'pd-badge pd-badge-danger';
    return 'pd-badge pd-badge-neutral';
  }

  return (
    <div className="pd-page">
      <div className="pd-toolbar">
        <div className="pd-toolbar-left">
          <BackButton onClick={function(){ navigate(-1); }} />
          <h2 className="pd-title">Transaction Details</h2>
        </div>
      </div>

      <div className="pd-usercard">
        {user.image && (
          <div className="pd-user-avatar">
            <img className="pd-user-img" src={user.image} alt={'Image of ' + (user.name || user.username)} />
          </div>
        )}
        <div className="pd-user-main">
          <h3 className="pd-user-name">{user.username}</h3>
          <div className="pd-user-grid">
            <div className="pd-field">
              <span className="pd-label">Email</span>
              <span className="pd-value">{user.email || 'N/A'}</span>
            </div>
            <div className="pd-field">
              <span className="pd-label">Phone</span>
              <span className="pd-value">{formatPhoneNumber(user.phone)}</span>
            </div>
            <div className="pd-field">
              <span className="pd-label">Store Location</span>
              <span className="pd-value">{user.storeLocation || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pd-card">
        <div className="pd-rows">
          <div className="pd-row">
            <span className="pd-k">Booking ID</span>
            <span className="pd-v">{transaction.id}</span>
          </div>
          <div className="pd-row">
            <span className="pd-k">Service</span>
            <span className="pd-v">{transaction.bookingType}</span>
          </div>
          <div className="pd-row">
            <span className="pd-k">Guests</span>
            <span className="pd-v">{guests}</span>
          </div>
          <div className="pd-row">
            <span className="pd-k">Start</span>
            <span className="pd-v">{transaction.startTime ? formatDateTime(transaction.startTime) : 'N/A'}</span>
          </div>
          <div className="pd-row">
            <span className="pd-k">End</span>
            <span className="pd-v">{transaction.endTime ? formatDateTime(transaction.endTime) : 'N/A'}</span>
          </div>
          <div className="pd-row">
            <span className="pd-k">Price</span>
            <span className="pd-v">{formatMoney(finalPrice)}</span>
          </div>
          <div className="pd-row">
            <span className="pd-k">Status</span>
            <span className="pd-v">
              <span className={statusBadgeClass(transaction.status)}>
                {toSafeLower(transaction.status) || 'unknown'}
              </span>
            </span>
          </div>
        </div>

        {(additionalServices && (additionalServices.shuttleOptions || additionalServices.additionalServices || additionalServices.trainingOptions)) && (
          <div className="pd-addons">
            {renderAdditionalGroup('Shuttle Details', additionalServices.shuttleOptions)}
            {renderAdditionalGroup('Daycare Details', additionalServices.additionalServices)}
            {renderAdditionalGroup('Training Details', additionalServices.trainingOptions)}
          </div>
        )}

        <div className="pd-notes">
          <label className="pd-notes-label">Special Notes</label>
          <textarea
            className="pd-notes-input"
            value={notes}
            onChange={function (e) { setNotes(e.target.value); }}
            disabled={!canManage}
            placeholder={canManage ? 'Type notes for this booking…' : 'No permission to edit notes'}
          />
          {!canManage && <div className="pd-note-permission">You don’t have permission to edit this booking.</div>}
        </div>
      </div>

      <div className="pd-actions">
        {canUpdate && (
          <button className="pd-btn pd-btn-primary" onClick={handleUpdate}>Update</button>
        )}
        {canCancel && (
          <button className="pd-btn pd-btn-danger" onClick={handleCancelClick}>Cancel</button>
        )}
      </div>

      {showCancelModal && (
        <div className="pd-modal-overlay">
          <div className="pd-modal">
            <h3 className="pd-modal-title">Confirm Cancellation</h3>
            <p className="pd-modal-text">Are you sure you want to cancel this booking?</p>
            <div className="pd-modal-actions">
              <button className="pd-btn pd-btn-danger" onClick={confirmCancel}>Yes, Cancel</button>
              <button className="pd-btn pd-btn-ghost" onClick={function(){ setShowCancelModal(false); }}>No, Go Back</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPaymentDetails;
